import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { paymentReceiptPdfPath } from "@shared/paymentReceiptUrl";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { statementActivityBadgeClass } from "@shared/clarityOneStatementActivity";
import {
  formatStatementMoneySgd,
  statementConsistencyFingerprint,
  type ClarityOneStatementDateRange,
  type ClarityOneStatementOfAccount,
  type ClarityOneStatementTransaction,
} from "@shared/clarityOneStatementOfAccount";
import { formatDate } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

function formatTxnDate(value: string | null | undefined): string {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return formatDate(value);
    } catch {
      return value.slice(0, 10);
    }
  }
  return value;
}

function invoiceHref(kind: "portal" | "deal", token: string): string {
  return kind === "portal" ? `/invoices/portal/${token}` : `/invoices/deal/${token}`;
}

function ActionCell({ txn }: { txn: ClarityOneStatementTransaction }) {
  if (txn.action.type === "view_invoice") {
    return (
      <Button asChild size="sm" variant="outline" className="border-[#E8E4DC]">
        <Link href={invoiceHref(txn.action.kind, txn.action.token)}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          View Invoice
        </Link>
      </Button>
    );
  }
  if (txn.action.type === "view_receipt") {
    return (
      <Button asChild size="sm" variant="outline" className="border-[#E8E4DC]">
        <a href={paymentReceiptPdfPath(txn.action.kind, txn.action.token)}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          Download Receipt
        </a>
      </Button>
    );
  }
  if (txn.action.type === "view_proposal") {
    return (
      <Button asChild size="sm" variant="outline" className="border-[#E8E4DC]">
        <Link href={`/proposals/${txn.action.proposalId}`}>View Proposal</Link>
      </Button>
    );
  }
  if (txn.action.type === "view_vo") {
    return (
      <Button asChild size="sm" variant="outline" className="border-[#E8E4DC]">
        <Link href={`/vo/${txn.action.token}`}>View Variation Order</Link>
      </Button>
    );
  }
  return <span className="text-[#8B7355]">—</span>;
}

export default function ClarityOneStatementPage() {
  const [, setLocation] = useLocation();
  const [proposalFilter, setProposalFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<ClarityOneStatementDateRange>("all");
  const [search, setSearch] = useState("");

  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const queryInput = useMemo(
    () => ({
      proposalId:
        proposalFilter !== "all" && Number(proposalFilter) > 0 ? Number(proposalFilter) : null,
      dateRange,
      status: "all" as const,
    }),
    [proposalFilter, dateRange],
  );

  const { data, isLoading, error } = trpc.clarityOneRecords.getStatementOfAccount.useQuery(
    queryInput,
    {
      enabled: !!me,
      refetchOnWindowFocus: false,
    },
  );

  const downloadStatement = trpc.clarityOneRecords.downloadStatementPdf.useMutation({
    onSuccess: (res) => {
      if (data) {
        const live = statementConsistencyFingerprint(data);
        if (live !== res.fingerprint) {
          toast.error("Statement figures changed. Please refresh and try again.");
          return;
        }
      }
      const bytes = Uint8Array.from(atob(res.base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (e) => toast.error(e.message || "Could not download statement"),
  });

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/account/statement"));
  }, [meLoading, me, setLocation]);

  const statement = data as ClarityOneStatementOfAccount | undefined;
  const projectOptions = statement?.projects ?? statement?.proposals ?? [];
  const projectCards = statement?.proposals ?? [];

  const filteredActivities = useMemo(() => {
    const rows = statement?.activities ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((t) => {
      const hay = [
        t.activityLabel,
        t.details,
        t.reference,
        t.projectTitle,
        t.activityBadge,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [statement?.activities, search]);

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  return (
    <ClarityOneLayout showNav>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-light text-[#1A2744] tracking-tight">Account Statement</h1>
          <p className="text-sm text-[#B8956E] mt-2">
            Customer: <span className="font-medium">{me.name || "—"}</span>
          </p>
        </div>
        <Button
          type="button"
          className="bg-[#1A2744] text-white hover:bg-[#1A2744]/90 rounded-xl"
          disabled={downloadStatement.isPending || isLoading}
          onClick={() => downloadStatement.mutate(queryInput)}
        >
          {downloadStatement.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download Statement (PDF)
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-5">
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">Outstanding</p>
          <p className="text-2xl font-medium text-[#1A2744]" data-testid="soa-outstanding">
            {isLoading ? "…" : formatStatementMoneySgd(statement?.summary.outstanding ?? 0)}
          </p>
          <p className="text-xs text-[#8B7355] mt-2">Remaining contract balance</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-5">
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">Due</p>
          <p className="text-2xl font-medium text-[#1A2744]" data-testid="soa-due">
            {isLoading ? "…" : formatStatementMoneySgd(statement?.summary.due ?? 0)}
          </p>
          <p className="text-xs text-[#8B7355] mt-2">Unpaid issued invoices</p>
        </div>
      </div>

      {!isLoading && projectCards.length > 0 ? (
        <div className="mb-8" data-testid="soa-project-breakdown">
          <h2 className="text-sm tracking-[0.18em] uppercase text-[#B8956E] mb-3">Projects</h2>
          <div className="grid grid-cols-1 gap-3">
            {projectCards.map((p) => (
              <div
                key={p.proposalId}
                className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-4"
              >
                <p className="text-sm font-medium text-[#1A2744]">
                  {p.proposalRef} · {p.title}
                </p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Proposal Value</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(p.proposalValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Billed</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(p.billed)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Paid</p>
                    <p className="text-[#2E7D32] mt-1 tabular-nums">
                      {formatStatementMoneySgd(p.paid)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Outstanding</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(p.outstanding)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Due</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(p.due)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {statement?.otherActivity ? (
              <div className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-4">
                <p className="text-sm font-medium text-[#1A2744]">
                  {statement.otherActivity.title}
                </p>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Billed</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(statement.otherActivity.billed)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Paid</p>
                    <p className="text-[#2E7D32] mt-1 tabular-nums">
                      {formatStatementMoneySgd(statement.otherActivity.paid)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Outstanding</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(statement.otherActivity.outstanding)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#B8956E] uppercase tracking-wide">Due</p>
                    <p className="text-[#1A2744] mt-1 tabular-nums">
                      {formatStatementMoneySgd(statement.otherActivity.due)}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <label className="flex-1 text-sm text-[#8B7355]">
          <span className="block text-[10px] uppercase tracking-wide mb-1">Filter by project</span>
          <select
            className="w-full h-11 rounded-xl border border-[#E8E4DC] bg-white px-3 text-[#1A2744]"
            value={proposalFilter}
            onChange={(e) => setProposalFilter(e.target.value)}
            data-testid="soa-project-filter"
          >
            <option value="all">All Projects</option>
            {projectOptions.map((p) => (
              <option key={p.proposalId} value={String(p.proposalId)}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
        <label className="flex-1 text-sm text-[#8B7355]">
          <span className="block text-[10px] uppercase tracking-wide mb-1">Date</span>
          <select
            className="w-full h-11 rounded-xl border border-[#E8E4DC] bg-white px-3 text-[#1A2744]"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as ClarityOneStatementDateRange)}
            data-testid="soa-date-filter"
          >
            <option value="all">All Time</option>
            <option value="last_30_days">Last 30 days</option>
            <option value="last_90_days">Last 90 days</option>
            <option value="this_year">This year</option>
          </select>
        </label>
        <label className="flex-[1.4] text-sm text-[#8B7355]">
          <span className="block text-[10px] uppercase tracking-wide mb-1">Search</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B8956E]" />
            <input
              className="w-full h-11 rounded-xl border border-[#E8E4DC] bg-white pl-10 pr-3 text-[#1A2744] placeholder:text-[#B8A990]"
              placeholder="Search activity or reference..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="soa-search"
            />
          </div>
        </label>
      </div>

      {error ? (
        <p className="text-sm text-[#8B7355]">We could not load your statement. Please try again.</p>
      ) : null}

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" />
      ) : !filteredActivities.length ? (
        <p className="text-sm text-[#8B7355]" data-testid="soa-empty">
          No commercial activity on this account yet.
        </p>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E4DC] px-4 py-4 sm:px-6 mb-10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm" data-testid="soa-ledger">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-[#B8956E] border-b border-[#E8E4DC]">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Activity</th>
                  <th className="py-2 pr-3 font-medium">Project</th>
                  <th className="py-2 pr-3 font-medium text-right">Contract / Invoice</th>
                  <th className="py-2 pr-3 font-medium text-right">Payment</th>
                  <th className="py-2 pr-3 font-medium text-right">Outstanding</th>
                  <th className="py-2 pr-3 font-medium text-right">Due</th>
                  <th className="py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.map((txn) => (
                  <tr key={txn.id} className="border-b border-[#F0EDE6] align-top">
                    <td className="py-3 pr-3 text-[#1A2744] whitespace-nowrap">
                      {formatTxnDate(txn.date)}
                    </td>
                    <td className="py-3 pr-3 text-[#1A2744]">
                      <div className="flex items-start gap-2">
                        <span
                          className={`mt-0.5 inline-flex shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide ${statementActivityBadgeClass(txn.activityBadge)}`}
                        >
                          {txn.activityBadge}
                        </span>
                        <span>{txn.activityLabel || txn.details}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-[#1A2744]">{txn.projectTitle || "—"}</td>
                    <td className="py-3 pr-3 text-right text-[#1A2744] tabular-nums">
                      {txn.billed > 0 ? formatStatementMoneySgd(txn.billed) : "—"}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums text-[#2E7D32]">
                      {txn.paidCredited > 0 ? formatStatementMoneySgd(txn.paidCredited) : "—"}
                    </td>
                    <td className="py-3 pr-3 text-right text-[#1A2744] tabular-nums">
                      {formatStatementMoneySgd(txn.runningOutstanding)}
                    </td>
                    <td className="py-3 pr-3 text-right text-[#1A2744] tabular-nums">
                      {formatStatementMoneySgd(txn.runningDue)}
                    </td>
                    <td className="py-3">
                      <ActionCell txn={txn} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#8B7355] mt-4 text-center">
            Showing {filteredActivities.length} of {statement?.activities.length ?? 0} activities
          </p>
        </div>
      )}

      <p className="text-xs text-[#8B7355] flex items-start gap-2 mt-8">
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#1A2744]/10 text-[#1A2744] text-[10px] mt-0.5">
          i
        </span>
        Outstanding is the unpaid portion of approved contracts. Due is the unpaid portion of
        invoices already issued. Outstanding is not automatically due.
      </p>
    </ClarityOneLayout>
  );
}
